<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ hotelSpiderName }}
    </portal>
    <template v-if="config !== null">
      <card :title="$t('admin.interfaces.pageHotelSpider.card.general')" cols="12 6@desktop">
        <hm-card-inner>
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.enabled')"
                           :enabled-title="$t('enabled')"
                           :disabled-title="$t('disabled')"
                           :disabled="!canEnableHotelSpider"
                           v-model="config.enabled"
                           cols="12 6@hd start-1"
                           name="enabled" />
        </hm-card-inner>
        <hm-card-inner v-if="!config.enabled" :full-size="true">
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Interface is <b><u>DISABLED</u></b>. Reservations will <b><u>NOT</u></b> be imported. Availability changes will <b><u>NOT</u></b> be synced. Price changes will <b><u>NOT</u></b> be synced.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.temporaryDisabled')"
                           v-model="config.temporaryDisabled"
                           :disabled="!hasRole('ROLE_SYSADMIN')"
                           cols="12 6@hd start-1"
                           name="temporaryDisabled" />
          <hm-input :label="$t('admin.interfaces.pageHotelSpider.hotelId')"
                    v-model="config.hotelId"
                    cols="12 6@hd start-1"
                    name="hotelId" />
          <hm-input-select v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           v-model="config.whiteLabelOverride"
                           :label="`${$t('admin.interfaces.pageHotelSpider.whiteLabelOverride')} 🔒`"
                           cols="12 6@hd start-1"
                           name="whiteLabelOverride">
            <option :value="null"></option>
            <option v-for="itEnum in enums['WhiteLabelPartner']" :value="itEnum" :key="itEnum">{{ itEnum }}</option>
          </hm-input-select>
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.autoPullReservations')"
                           v-model="config.autoPullReservations"
                           cols="12 6@hd start-1"
                           name="autoPullReservations" />
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.updateRates')"
                           v-model="config.updateRates"
                           cols="12 6@hd start-1"
                           name="updateRates" />
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.updateRestrictions')"
                           v-model="config.updateRestrictions"
                           cols="12 6@hd start-1"
                           name="updateRestrictions" />
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.updateInventory')"
                           v-model="config.updateInventory"
                           cols="12 6@hd start-1"
                           name="updateInventory" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.interfaces.pageHotelSpider.card.additionalSetings')" cols="12 6@desktop start-7@desktop" rows="start-1@desktop 1">
        <template v-if="Object.values(config.ratePlanMap).map(it => findHotelSettingById('ratePlans', it)).filter(it => !isNil(it)).map(it => it.rateEntryMode).filter(it => it === 'Seasonal').length > 0 || hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')">
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.autoCloseUnmappedRates')"
                           v-model="config.autoCloseUnmappedRates"
                           cols="12 6@hd start-1"
                           name="autoCloseUnmappedRates" />
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.autoCloseUnmappedRoomTypes')"
                           v-model="config.autoCloseUnmappedRoomTypes"
                           cols="12 6@hd start-1"
                           name="autoCloseUnmappedRoomTypes" />
          <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.autoCloseNoRate')"
                           v-model="config.autoCloseNoRate"
                           cols="12 6@hd start-1"
                           name="autoCloseNoRate" />
        </template>
        <hm-input-toggle :label="$t('admin.interfaces.pageHotelSpider.roomBasedPricingEnabled')"
                         :help="$t('admin.interfaces.pageHotelSpider.roomBasedPricingEnabled_help')"
                         v-model="config.roomBasedPricingEnabled"
                         cols="12 6@hd start-1"
                         name="roomBasedPricingEnabled" />
        <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN')"
                         :label="$t('admin.interfaces.pageHotelSpider.debugEnabled')"
                         v-model="config.debugEnabled"
                         cols="12 6@hd start-1"
                         name="debugEnabled" />
        <hm-input-money v-model="config.fallbackRate"
                        :currency="hotelSettings.details.currency"
                        :label="$t('admin.interfaces.pageHotelSpider.fallbackRate')"
                        :help="$t('admin.interfaces.pageHotelSpider.fallbackRate_help')"
                        name="fallbackRate"
                        cols="12 6@hd start-1" />
        <hm-input-select v-model="config.childAgeCategoryId"
                         :label="$t('admin.interfaces.pageHotelSpider.childAgeCategoryId')"
                         :help="$t('admin.interfaces.pageHotelSpider.childAgeCategoryId_help')"
                         name="childAgeCategoryId"
                         cols="12 6@hd start-1">
          <option :value="null"></option>
          <option v-for="item in hotelSettings.ageCategories.all(config.childAgeCategoryId)" v-if="item.ageQualifier === 'Adult'" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
          <option v-for="item in hotelSettings.ageCategories.all(config.childAgeCategoryId)" v-if="item.ageQualifier !== 'Adult' && !item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]} (${item.ageFrom} - ${item.ageUntil})`"></option>
        </hm-input-select>
      </card>
      <card :title="$t('admin.interfaces.pageHotelSpider.card.mapping')" cols="12 6@desktop start-1@desktop" rows="start-2@desktop 3">
        <!--        <template v-if="Object.values(config.ratePlanMap).map(it => findHotelSettingById('ratePlans', it)).filter(it => !isNil(it)).map(it => it.rateEntryMode).filter(it => it === 'Daily').length >= 1">-->
        <!--          <card-badge :tooltip="$t('betaVersion.tooltip')">{{ $t('betaVersion.title') }}</card-badge>-->
        <!--        </template>-->
        <hm-card-inner v-if="!isNil(config.roomTypes)" :title="$t('admin.interfaces.pageHotelSpider.card.roomTypeMapping')">
          <template v-for="mappingItem in config.roomTypes">
            <hm-input-select v-model="config.roomTypeMap[mappingItem.id]"
                             :label="`${mappingItem.name} (${mappingItem.id})`"
                             :key="mappingItem.id"
                             name="roomTypeMapping"
                             cols="12 6@hd start-1">
              <option :value="null"></option>
              <option v-for="item in hotelSettings.roomTypes.all(config.roomTypeMap[mappingItem.id])" v-if="!item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            </hm-input-select>
          </template>
        </hm-card-inner>
        <hm-card-inner v-if="!isNil(config.ratePlans)" :title="$t('admin.interfaces.pageHotelSpider.card.ratePlanMapping')">
          <template v-for="mappingItem in config.ratePlans">
            <hm-input-select v-model="config.ratePlanMap[mappingItem.id]"
                             :label="`${mappingItem.name} (${mappingItem.id})`"
                             :key="mappingItem.id"
                             name="ratePlanMapping"
                             cols="12 6@hd start-1">
              <option :value="null"></option>
              <option v-for="item in hotelSettings.ratePlans.all(config.ratePlanMap[mappingItem.id])"
                      v-if="!item.deleted && item.ratePlanType === 'DayRate'"
                      :value="item.id"
                      v-html="`${item.name[$shortLocale]} ${item.rateEntryMode === 'Daily' ? '(β-Version)' : ''}`"></option>
            </hm-input-select>
          </template>
        </hm-card-inner>
        <hm-card-inner v-if="!isNil(config.serviceIdMap) && hasRole('ROLE_SYSADMIN')" :full-size="true" title="Services">
          <hm-table :hover="false"
                    min-width="300px"
                    columns="2"
                    grid-template-columns="1fr 1fr">
            <template slot="table-head">
              <div class="hm-table__th">Channel-Manager Service ID</div>
              <div class="hm-table__th">Additional Service</div>
            </template>
            <template v-for="(value, idx) in config.serviceIdMap">
              <div class="hm-table__td">
                <hm-input :inline="true"
                          :id="`key-${idx}`"
                          v-model="value[0]"
                          label="Channel-Manager Service ID"
                          name="key" />
              </div>
              <div class="hm-table__td">
                <hm-input-select v-model="value[1]"
                                 :inline="true"
                                 :id="`value-${idx}`"
                                 label="Additional Service"
                                 name="value"
                                 cols="12 6@hd start-1">
                  <option :value="null"></option>
                  <option v-for="item in hotelSettings.inHouseServices.all(value[1])" v-if="!item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
                </hm-input-select>
              </div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
      <card :title="$t('admin.interfaces.pageHotelSpider.card.ignoreUpdates')" cols="12 6@desktop start-7@desktop">
        <hm-multiselect id="ingoreChannelRoomTypeIds"
                        v-model="config.ignoreChannelRoomTypeIds"
                        :options="config.roomTypes"
                        :label="$t('admin.interfaces.pageHotelSpider.ignoreChannelRoomTypeIds')"
                        cols="12 6@hd start-1"
                        name="ignoreChannelRoomTypeIds"
                        label-expression="${option.name}" />
        <hm-multiselect id="ingoreChannelRatePlanIds"
                        v-model="config.ignoreChannelRatePlanIds"
                        :options="config.ratePlans"
                        :label="$t('admin.interfaces.pageHotelSpider.ignoreChannelRatePlanIds')"
                        cols="12 6@hd start-1"
                        name="ignoreChannelRatePlanIds"
                        label-expression="${option.name}" />
      </card>
      <card :title="$t('admin.interfaces.pageHotelSpider.card.credentials')" cols="12 6@desktop start-7@desktop" rows="start-2@desktop">
        <hm-card-inner>
          <hm-col cols="12">
            <div style="position:relative; margin-bottom: 0.8rem;">{{ $t('admin.interfaces.pageHotelSpider.hintCredentials') }}</div>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input :label="`${$t('admin.interfaces.pageHotelSpider.xmlUsername')} 🔒`"
                    v-model="config.xmlUsername"
                    :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                    autocomplete="hotelspider-xml-interface-username"
                    cols="12 6@hd start-1"
                    name="xmlUsername" />
          <hm-input :label="`${$t('admin.interfaces.pageHotelSpider.xmlPassword')} 🔒`"
                    v-model="config.xmlPassword"
                    :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                    autocomplete="hotelspider-xml-interface-password"
                    cols="12 6@hd"
                    type="password"
                    name="xmlPassword" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.interfaces.pageHotelSpider.card.bookingCom')" cols="12" card-type="collapsible">
        <hm-card-inner :title="$t('admin.interfaces.pageHotelSpider.card.bookingComServiceMap')">
          <template v-for="itEnum in enums['BookingServiceType']">
            <hm-input-select v-model="config.bookingServiceMap[itEnum]"
                             :label="`${itEnum} (${enums.ADDITIONAL_DATA.BookingServiceType ? enums.ADDITIONAL_DATA.BookingServiceType[itEnum].code : '-'})`"
                             :key="itEnum"
                             name="bookingServiceMap"
                             cols="12 6@hd start-1">
              <option :value="null"></option>
              <option v-for="item in hotelSettings.inHouseServices.all(config.bookingServiceMap[itEnum])" v-if="!item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            </hm-input-select>
          </template>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageHotelSpider.card.bookingComTaxFeeMap')">
          <template v-for="itEnum in enums['FeeTaxType']">
            <hm-input-select v-model="config.taxMap[itEnum]"
                             :label="`${itEnum} (${enums.ADDITIONAL_DATA.FeeTaxType ? enums.ADDITIONAL_DATA.FeeTaxType[itEnum].code : '-'})`"
                             :key="itEnum"
                             name="taxMap"
                             cols="12 6@hd start-1">
              <option :value="null"></option>
              <option v-for="item in hotelSettings.inHouseServices.all(config.taxMap[itEnum])" v-if="!item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            </hm-input-select>
          </template>
        </hm-card-inner>
      </card>
    </template>
    <hm-floating-buttons>
      <hm-floating-button :label="$t('admin.interfaces.pageHotelSpider.actions.more')" icon="fill-more" color="gray">
        <hm-floating-child-button :throttle="30 * 1000"
                                  :label="$t('admin.interfaces.pageHotelSpider.actions.retrieveReservations')"
                                  icon="download"
                                  color="mint"
                                  @click="retrieveReservations()" />
        <hm-floating-child-button :throttle="120 * 1000"
                                  :label="$t('admin.interfaces.pageHotelSpider.actions.updateRates')"
                                  icon="upload"
                                  color="mint"
                                  @click="updateRates()" />
        <hm-floating-child-button :throttle="120 * 1000"
                                  :label="$t('admin.interfaces.pageHotelSpider.actions.updateAvailability')"
                                  icon="upload"
                                  color="mint"
                                  @click="updateAvailability()" />
      </hm-floating-button>
      <hm-floating-button :throttle="3500"
                          :label="$t('admin.interfaces.pageHotelSpider.actions.reloadData')"
                          icon="download"
                          color="orange"
                          @click="refreshData()" />
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import Card from '../../../lib/layout/Card';
import HmInputCheckbox from '../../../lib/inputs/HmInputCheckbox';
import HmInputMoney from '../../../lib/inputs/HmInputMoney';
import HmCardInner from '../../../lib/layout/HmCardInner';

export default {
  name: 'PageHotelSpider',
  components: { HmCardInner, HmInputMoney, HmInputCheckbox, Card },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'config',
      variable: 'config',
      fn: function (cbSaveComplete) {
        const config = cloneDeep(this.config);

        if (!isEmpty(config.serviceIdMap)) {
          config.serviceIdMap = Object.fromEntries(config.serviceIdMap.filter(it => !isEmpty(it[0]) && !isEmpty(it[1])));
        }

        this.axios.put(this.serviceUrls.interfaces.hotelSpider.get(this, this.config.id), config)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      config: null,
    };
  },

  computed: {
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    canEnableHotelSpider: function () {
      if (isNil(this.config)) return true;
      return !isEmpty(this.config.roomTypes)
        && !isEmpty(this.config.ratePlans)
        && !isEmpty(this.config.hotelId);
    },
  },

  watch: {
    'config.serviceIdMap': {
      handler: function (newValue) {
        if (!isNil(newValue)) {
          let tmp = cloneDeep(newValue);
          tmp = tmp.filter(it => !isEmpty(it[0]) || !isEmpty(it[1]));
          tmp.push(['', '']);

          if (tmp.length !== newValue.length) {
            this.config.serviceIdMap = tmp;
          } else if (newValue.length > 0 && (!isEmpty(newValue[newValue.length - 1][0]) || !isEmpty(newValue[newValue.length - 1][1]))) {
            this.config.serviceIdMap = tmp;
          }
        }
      },
      deep: true,
    },
    canEnableHotelSpider: function (newValue) {
      if (newValue === false && !isEmpty(this.config.hotelId)) {
        this.config.enabled = false;
      }
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.interfaces.hotelSpider.get(this, null))
        .then((response) => {
          if (isNil(this.config)) {
            const config = response.data.data;
            if (!isNil(config.serviceIdMap)) config.serviceIdMap = Object.entries(config.serviceIdMap);
            this.config = config;
            if (this.hotelSettings.hotel.whiteLabel === 'Rainmaker') {
              this.config.whiteLabelOverride = 'Hotelmeister';
            }
          }
        });
    },
    refreshData() {
      this.axios.post(this.serviceUrls.interfaces.hotelSpider.refreshData(this, null))
        .then((response) => {
          const config = response.data.data;
          if (!isNil(config.serviceIdMap)) config.serviceIdMap = Object.entries(config.serviceIdMap);
          this.config = config;
          if (this.hotelSettings.hotel.whiteLabel === 'Rainmaker') {
            this.config.whiteLabelOverride = 'Hotelmeister';
          }
          this.showSuccessMessage();
        })
        .catch(() => {
          this.showErrorMessage();
        });
    },
    updateRates() {
      this.axios.post(this.serviceUrls.interfaces.hotelSpider.updateRates(this, null))
        .then((response) => {
          this.showSuccessMessage();
        });
    },
    updateAvailability() {
      this.axios.post(this.serviceUrls.interfaces.hotelSpider.updateAvailability(this, null))
        .then((response) => {
          this.showSuccessMessage();
        });
    },
    retrieveReservations() {
      this.axios.post(this.serviceUrls.interfaces.hotelSpider.retrieveReservations(this, null))
        .then((response) => {
          this.showSuccessMessage();
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/interfaces/PageHotelSpider.vue